import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useOrgMemberStore } from '@/store/orgMember';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { academyService } from '@/services/academy';
import { hasRole } from '@court-sync/shared';
import type { PublicOrganizationProfile } from '@/services/publicOrg';

/**
 * Syncs org profile and academy context into consumer stores for nav gating and member APIs.
 */
export function OrgConsumerContextSync({ profile }: { profile: PublicOrganizationProfile }) {
  const orgSlug = useOrgSlug();
  const { user } = useAuthStore();
  const { host, academy: hostAcademy } = useOrgHost();
  const { setOrgContext, clearOrgContext } = useOrgMemberStore();
  const { setAcademyContext, clearAcademyContext } = useGuardianAcademyStore();
  const [error, setError] = useState<string | null>(null);

  const academySlug = profile.academySlug ?? hostAcademy?.slug ?? undefined;
  const userHasLessonsRole = Boolean(user && (hasRole(user, 'guardian') || hasRole(user, 'student')));
  const shouldLoadAcademy = Boolean(userHasLessonsRole && profile.modules.hasAcademy && academySlug);
  const effectiveAcademySlug = shouldLoadAcademy ? academySlug : undefined;

  useEffect(() => {
    if (!profile || !orgSlug) {
      clearOrgContext();
      return;
    }
    setOrgContext(orgSlug, profile, null, profile.clubSlug ?? null);
  }, [profile, orgSlug, setOrgContext, clearOrgContext]);

  useEffect(() => {
    if (!shouldLoadAcademy || !effectiveAcademySlug) {
      clearAcademyContext();
      return undefined;
    }

    const existingSlug = useGuardianAcademyStore.getState().academySlug;
    if (existingSlug && existingSlug !== effectiveAcademySlug) {
      clearAcademyContext();
    }

    if (host.mode === 'subdomain' && hostAcademy) {
      void Promise.resolve().then(() => {
        setAcademyContext(effectiveAcademySlug, hostAcademy.id, hostAcademy);
        if (profile && orgSlug) {
          setOrgContext(orgSlug, profile, hostAcademy, profile.clubSlug ?? null);
        }
        setError(null);
      });
      return () => clearAcademyContext();
    }

    let mounted = true;
    academyService
      .resolveAcademyBySlug(effectiveAcademySlug)
      .then((acad) => {
        if (!mounted) return;
        setAcademyContext(effectiveAcademySlug, acad.id, acad);
        if (profile && orgSlug) {
          setOrgContext(orgSlug, profile, acad, profile.clubSlug ?? null);
        }
        setError(null);
      })
      .catch((err) => {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Academy not found');
        }
      });
    return () => {
      mounted = false;
    };
  }, [
    shouldLoadAcademy,
    effectiveAcademySlug,
    host.mode,
    hostAcademy,
    profile,
    orgSlug,
    setAcademyContext,
    clearAcademyContext,
    setOrgContext,
  ]);

  if (error) {
    return null;
  }

  return null;
}
